// ── «Hoy»: the nudges of the modules, in the reader's language ───────────────
//
// The backend sends data — a key of the module that owns it, its parameters,
// the English fallback, dates as YYYY-MM-DD — and the sentence is written here,
// by rules and with the reader's catalogue: «Hoy: 6 órdenes hoy; 2 órdenes
// atrasadas; ITV de Furgoneta 3 el viernes». No model is asked; there is
// nothing a model would write better than a template, every morning, for
// everybody.
//
// Pure on purpose — no Vue, no store — so the test composes the same summary
// with the real catalogues of the four languages (tests/today.test.js).

/**
 * A YYYY-MM-DD as the reader says it: the weekday within the coming week
 * («viernes», «divendres»), the date beyond it («3 nov»).
 */
export function formatDay(iso, { locale = 'es', today = null } = {}) {
  const [year, month, day] = String(iso ?? '').split('-').map(Number)
  if (!year || !month || !day) return String(iso ?? '')
  const date = new Date(Date.UTC(year, month - 1, day, 12))
  const from = today ? new Date(`${today}T12:00:00Z`) : null
  const ahead = from ? Math.round((date - from) / 86_400_000) : null
  const options = ahead !== null && ahead >= 0 && ahead < 7
    ? { weekday: 'long', timeZone: 'UTC' }
    : { day: 'numeric', month: 'short', timeZone: 'UTC' }
  try {
    return new Intl.DateTimeFormat(locale || 'es', options).format(date)
  } catch {
    return new Intl.DateTimeFormat('es', options).format(date)
  }
}

/**
 * The lines of the card and the sentence of the summary.
 *
 * `t`/`te` are vue-i18n's: a key the reader's catalogue does not have falls
 * back to the English the module sent, never to the bare key.
 *
 * @returns {{ lines: object[], summary: string }}
 */
export function composeToday(items, { t, te, locale = 'es', today = null } = {}) {
  const say = (key, params, fallback) => (key && te(key) ? t(key, params ?? {}) : fallback)

  const lines = (items ?? []).map((item) => {
    const dated = Object.fromEntries(Object.entries(item.dates ?? {})
      .map(([name, iso]) => [name, formatDay(iso, { locale, today })]))
    return {
      key: `${item.nudge}:${item.key}`,
      text: say(item.key, { ...item.params, ...dated }, item.text),
      link: item.link ?? null,
      level: item.level ?? 'info',
      module: item.module,
      ask: item.ask
        ? {
          question: say(item.ask.key, item.ask.params, item.ask.text),
          label: item.ask.labelKey && te(item.ask.labelKey) ? t(item.ask.labelKey) : null,
        }
        : null,
    }
  })

  const summary = lines.length
    ? t('assistant.today.lead', { items: lines.map(line => line.text).join(t('assistant.today.separator')) })
    : ''

  return { lines, summary }
}
